/**
 * W4S Cart to Quote
 * ----------------------------------------------------------------------------
 * Opens the quote dialog from any [data-w4s-quote-open] button (cart page or
 * cart drawer), collects the customer's details, reads the live cart from
 * /cart.js and renders a printable quote, then opens the browser print window
 * (where the customer can also "Save as PDF").
 *
 * All markup and styles live in the shadow root of <w4s-cart-quote>, so nothing
 * here can restyle the theme. The only page-level CSS is a print-isolation
 * <style> added immediately before printing and removed straight after.
 *
 * Optional opt-in:
 * - "Email me news and trade offers": when ticked (and marketingMode is 'live'), the email is
 *   subscribed in Shopify via the theme's customer form (same as the newsletter sign-ups),
 *   tagged per config.marketingTags. In 'simulated' mode it's only recorded.
 *
 * Store copy (config.notifyStore): every quote is also posted to Shopify's contact form, which
 * emails it to the store's contact address (ingested by Gorgias) with the customer as reply-to.
 * The full quote is in the email body as text (the contact form can't carry attachments).
 *
 * GA4 (config.ga4): fires generate_lead with value, currency, quote_number and lead_source.
 *
 * Klaviyo (if its onsite script is on the page and config.klaviyo is true): the customer is
 * identified with cart_quote_* profile properties and a "Requested Quote" event is tracked with
 * the full quote (number, items, totals, reorder link), for segments and quote-email flows.
 *
 * Reorder link ("Rebuild this cart"): built by assets/w4s-cart-share.js when it's on the page, so
 * it keeps line-item properties and selling plans. Without it, falls back to a cart permalink,
 * which can't, so it's left out when the cart has customised lines.
 *
 * Emits `w4s:quote:created` on document with { detail: { quote, marketing, klaviyo, storeCopy, ga4 } }.
 */
(() => {
  if (window.W4SCartQuote) return;

  const host = document.getElementById('w4s-cart-quote');
  if (!host) return;

  // Declarative shadow DOM is parsed by the browser; attach manually for older browsers.
  let root = host.shadowRoot;
  if (!root) {
    const template = host.querySelector('template');
    if (!template) return;
    root = host.attachShadow({ mode: 'open' });
    root.appendChild(template.content.cloneNode(true));
    template.remove();
  }

  const dialog = root.getElementById('dialog');
  const printRoot = root.querySelector('[data-print-root]');
  const configEl = root.querySelector('[data-config]');
  if (!dialog || !printRoot || !configEl) return;

  let config = {};
  try {
    config = JSON.parse(configEl.textContent);
  } catch (e) {
    return;
  }

  const form = root.querySelector('[data-form]');
  const errorEl = root.querySelector('[data-error]');
  const submitBtn = root.querySelector('[data-submit]');
  const STORAGE_KEY = 'w4s-quote-details';
  const REMEMBERED_FIELDS = ['first_name', 'last_name', 'email', 'company', 'phone'];
  const PRINT_STYLE_ID = 'w4s-cart-quote-print-isolation';
  const doneEl = root.querySelector('[data-done]');
  let opener = null;
  let lastQuote = null;

  // Must be a direct child of <body> so print isolation can hide everything else.
  if (host.parentElement !== document.body) document.body.appendChild(host);

  /* ---------------------------------------------------------------- helpers */

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const nl2br = (value) => escapeHtml(value).replace(/\r?\n/g, '<br>');

  const cartRoot = () => {
    const cartRoute = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
    return cartRoute.replace(/\/$/, '');
  };

  const makeMoneyFormatter = (currency) => {
    try {
      const fmt = new Intl.NumberFormat(config.locale || undefined, { style: 'currency', currency });
      return (cents) => fmt.format((Number(cents) || 0) / 100);
    } catch (e) {
      return (cents) => `${((Number(cents) || 0) / 100).toFixed(2)} ${currency}`;
    }
  };

  const formatDate = (date) => {
    try {
      return new Intl.DateTimeFormat(config.locale || undefined, { dateStyle: 'long' }).format(date);
    } catch (e) {
      return date.toDateString();
    }
  };

  const makeQuoteNumber = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${String(date.getFullYear()).slice(-2)}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
    const rand = Math.random().toString(36).slice(2, 6).toUpperCase().padEnd(4, '0');
    return `${config.prefix || 'Q'}-${stamp}-${rand}`;
  };

  const sizedImage = (url, width) => {
    if (!url) return '';
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const visibleProperties = (properties) => {
    if (!properties) return [];
    return Object.entries(properties).filter(([key, value]) => (
      key && !key.startsWith('_') && value !== null && value !== undefined && String(value).trim() !== ''
    ));
  };

  const showError = (message, isHtml = false) => {
    if (isHtml) {
      errorEl.innerHTML = message;
    } else {
      errorEl.textContent = message;
    }
    errorEl.hidden = !message;
  };

  const setLoading = (loading) => {
    submitBtn.disabled = loading;
    submitBtn.classList.toggle('is-loading', loading);
    submitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
  };

  /* --------------------------------------------------------- remembered data */

  const restoreGuestDetails = () => {
    if (config.isSignedIn) return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (!saved) return;
      REMEMBERED_FIELDS.forEach((name) => {
        const field = form.elements[name];
        if (field && !field.value && saved[name]) field.value = saved[name];
      });
    } catch (e) { /* storage unavailable */ }
  };

  const rememberGuestDetails = (details) => {
    if (config.isSignedIn) return;
    try {
      const toSave = {};
      REMEMBERED_FIELDS.forEach((name) => { toSave[name] = details[name] || ''; });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch (e) { /* storage unavailable */ }
  };

  /* ---------------------------------------------------------- marketing */

  /**
   * Marketing consent. In 'live' mode a ticked box subscribes the email in Shopify by posting
   * the theme's customer form (form_type=customer), exactly like the newsletter sign-ups:
   * Shopify creates or updates the customer with email marketing consent and adds the tags.
   * It never blocks the quote; the outcome is reported in the w4s:quote:created event.
   * Statuses: not_requested, simulated, subscribed, challenge (Shopify's bot check stopped it),
   * failed, timeout.
   */
  const subscribeToMarketing = async (email) => {
    const body = new URLSearchParams();
    body.set('form_type', 'customer');
    body.set('utf8', '\u2713');
    body.set('contact[email]', email);
    body.set('contact[tags]', config.marketingTags || 'prospect, newsletter');
    const res = await fetch(config.contactUrl || '/contact', {
      method: 'POST',
      body,
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { Accept: 'text/html' }
    });
    const url = new URL(res.url, window.location.origin);
    if (/\/challenge/.test(url.pathname)) return { status: 'challenge' };
    if (url.searchParams.get('customer_posted') === 'true') return { status: 'subscribed' };
    return { status: 'failed', httpStatus: res.status };
  };

  const handleMarketingConsent = async (details, consented) => {
    const record = {
      consented,
      email: details.email,
      source: 'cart_quote',
      mode: config.marketingMode || 'simulated',
      status: 'not_requested',
      at: new Date().toISOString()
    };
    if (!consented) return record;
    if (record.mode !== 'live') {
      record.status = 'simulated';
      console.info('[W4S Cart to Quote] Marketing consent captured (simulated \u2013 not sent):', record);
      return record;
    }
    try {
      const timeout = new Promise((resolve) => { setTimeout(() => resolve({ status: 'timeout' }), 8000); });
      Object.assign(record, await Promise.race([subscribeToMarketing(details.email), timeout]));
    } catch (err) {
      record.status = 'failed';
      record.error = String(err && err.message ? err.message : err);
    }
    if (record.status !== 'subscribed') console.warn('[W4S Cart to Quote] Marketing sign-up not confirmed:', record);
    return record;
  };

  /* ------------------------------------------------------ store copy */

  // Plain-text version of the quote for the store's email (Gorgias ticket).
  const quoteAsText = (quote, marketing) => {
    const money = makeMoneyFormatter(quote.currency);
    const c = quote.customer;
    const lines = [];
    lines.push(`QUOTE ${quote.number}`);
    lines.push(`Created: ${new Date(quote.createdAt).toUTCString()}`);
    lines.push('');
    lines.push(`Customer: ${[c.first_name, c.last_name].filter(Boolean).join(' ')}`);
    lines.push(`Email: ${c.email}`);
    if (c.company) lines.push(`Company: ${c.company}`);
    if (c.phone) lines.push(`Phone: ${c.phone}`);
    if (c.reference) lines.push(`Reference / notes: ${c.reference}`);
    lines.push(`Marketing opt-in: ${marketing && marketing.consented ? 'Yes' : 'No'}`);
    lines.push('');
    lines.push('ITEMS');
    quote.items.forEach((i, n) => {
      const name = `${i.title}${i.variant ? ` (${i.variant})` : ''}`;
      lines.push(`${n + 1}. ${name}`);
      const meta = [];
      if (i.sku) meta.push(`SKU ${i.sku}`);
      meta.push(`${i.quantity} \u00d7 ${money(i.unitPrice)} = ${money(i.lineTotal)}`);
      lines.push(`   ${meta.join('  |  ')}`);
      i.properties.forEach(([key, value]) => lines.push(`   ${key}: ${value}`));
      if (i.sellingPlan) lines.push(`   ${i.sellingPlan}`);
      i.discounts.filter((d) => d.amount > 0).forEach((d) => lines.push(`   Discount ${d.title || ''}: -${money(d.amount)}`));
      if (i.url) lines.push(`   ${i.url}`);
    });
    lines.push('');
    lines.push(`Subtotal: ${money(quote.subtotal)}`);
    quote.cartDiscounts.filter((d) => d.amount > 0).forEach((d) => lines.push(`Discount ${d.title || ''}: -${money(d.amount)}`));
    lines.push(`Total: ${money(quote.total)} ${quote.currency}`);
    if (quote.note) {
      lines.push('');
      lines.push(`Cart note: ${quote.note}`);
    }
    if (quote.orderLink) {
      lines.push('');
      lines.push(`Rebuild this cart: ${quote.orderLink}`);
    }
    return lines.join('\n');
  };

  /**
   * Posts the quote to Shopify's contact form (form_type=contact), which emails the store.
   * Returns { status: 'sent' | 'challenge' | 'failed' | 'timeout' | 'disabled' }. Never throws.
   */
  const sendStoreCopy = async (quote, marketing) => {
    if (!config.notifyStore) return { status: 'disabled' };
    const c = quote.customer;
    const body = new URLSearchParams();
    body.set('form_type', 'contact');
    body.set('utf8', '\u2713');
    body.set('contact[Name]', [c.first_name, c.last_name].filter(Boolean).join(' '));
    body.set('contact[email]', c.email);
    if (c.phone) body.set('contact[Phone]', c.phone);
    if (c.company) body.set('contact[Company]', c.company);
    body.set('contact[Quote number]', quote.number);
    body.set('contact[Quote total]', `${makeMoneyFormatter(quote.currency)(quote.total)} ${quote.currency}`);
    body.set('contact[body]', quoteAsText(quote, marketing));
    const post = fetch(config.contactUrl || '/contact', {
      method: 'POST',
      body,
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { Accept: 'text/html' }
    }).then((res) => {
      const url = new URL(res.url, window.location.origin);
      if (/\/challenge/.test(url.pathname)) return { status: 'challenge' };
      if (url.searchParams.get('contact_posted') === 'true') return { status: 'sent' };
      return { status: 'failed', httpStatus: res.status };
    });
    const timeout = new Promise((resolve) => { setTimeout(() => resolve({ status: 'timeout' }), 8000); });
    try {
      const result = await Promise.race([post, timeout]);
      if (result.status !== 'sent') console.warn('[W4S Cart to Quote] Store copy not confirmed:', result);
      return result;
    } catch (err) {
      console.warn('[W4S Cart to Quote] Store copy failed', err);
      return { status: 'failed', error: String(err && err.message ? err.message : err) };
    }
  };

  /* ---------------------------------------------------------------- GA4 */

  /**
   * GA4 recommended event generate_lead, fired once per created quote, with value, currency,
   * quote_number and lead_source. Sent via gtag (or the GTM dataLayer if gtag isn't on the
   * page) and published to Shopify customer events for custom pixels. Never throws.
   * Returns the channels it was sent to.
   */
  const trackLead = (quote) => {
    if (!config.ga4) return [];
    const params = {
      value: Math.round(Number(quote.total) || 0) / 100,
      currency: quote.currency,
      quote_number: quote.number,
      lead_source: 'cart_quote'
    };
    const sent = [];
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', params);
        sent.push('gtag');
      } else if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'generate_lead', ...params });
        sent.push('dataLayer');
      }
    } catch (e) { /* ignore */ }
    try {
      const analytics = window.Shopify && window.Shopify.analytics;
      if (analytics && typeof analytics.publish === 'function') {
        analytics.publish('generate_lead', params);
        sent.push('shopify');
      }
    } catch (e) { /* ignore */ }
    return sent;
  };

  /* ------------------------------------------------------------ klaviyo */

  const absUrl = (u) => (u && u.startsWith('//') ? `https:${u}` : u || '');
  const toMajor = (cents) => Math.round(Number(cents) || 0) / 100;

  /**
   * Identifies the customer and tracks "Requested Quote" in Klaviyo. Uses the current onsite
   * object (klaviyo.identify/track) or the legacy _learnq queue. Returns 'sent', 'disabled' or
   * 'not_loaded'. Never throws.
   */
  const sendToKlaviyo = (quote, marketing) => {
    if (!config.klaviyo) return 'disabled';
    try {
      const c = quote.customer;
      const profile = {
        email: c.email,
        first_name: c.first_name,
        last_name: c.last_name,
        organization: c.company || undefined,
        cart_quote_requested: true,
        cart_quote_last_number: quote.number,
        cart_quote_last_at: quote.createdAt,
        cart_quote_marketing_opt_in: !!marketing.consented
      };
      const discounts = quote.cartDiscounts.reduce((sum, d) => sum + (d.amount || 0), 0)
        + quote.items.reduce((sum, i) => sum + i.discounts.reduce((s2, d) => s2 + (d.amount || 0), 0), 0);
      const event = {
        $event_id: quote.number,
        $value: toMajor(quote.total),
        QuoteNumber: quote.number,
        CreatedAt: quote.createdAt,
        Currency: quote.currency,
        Subtotal: toMajor(quote.subtotal),
        Discounts: toMajor(discounts),
        Total: toMajor(quote.total),
        ItemCount: quote.items.reduce((sum, i) => sum + i.quantity, 0),
        ItemNames: quote.items.map((i) => i.title),
        Items: quote.items.map((i) => ({
          ProductID: i.productId,
          VariantID: i.variantId,
          ProductName: i.title,
          VariantName: i.variant,
          SKU: i.sku,
          Quantity: i.quantity,
          ItemPrice: toMajor(i.unitPrice),
          RowTotal: toMajor(i.lineTotal),
          ImageURL: absUrl(i.image),
          ProductURL: i.url,
          Properties: Object.fromEntries(i.properties)
        })),
        ReorderURL: quote.orderLink || '',
        Company: c.company || '',
        Phone: c.phone || '',
        Reference: c.reference || '',
        Note: quote.note,
        MarketingConsent: !!marketing.consented,
        Source: 'cart_quote'
      };

      const k = window.klaviyo;
      if (k && typeof k.identify === 'function' && typeof k.track === 'function') {
        Promise.resolve(k.identify(profile))
          .then(() => k.track('Requested Quote', event))
          .catch((err) => console.warn('[W4S Cart to Quote] Klaviyo call failed', err));
        return 'sent';
      }
      if (window._learnq && typeof window._learnq.push === 'function') {
        const legacy = {
          $email: profile.email,
          $first_name: profile.first_name,
          $last_name: profile.last_name,
          $organization: profile.organization,
          cart_quote_requested: true,
          cart_quote_last_number: profile.cart_quote_last_number,
          cart_quote_last_at: profile.cart_quote_last_at,
          cart_quote_marketing_opt_in: profile.cart_quote_marketing_opt_in
        };
        window._learnq.push(['identify', legacy]);
        window._learnq.push(['track', 'Requested Quote', event]);
        return 'sent';
      }
      return 'not_loaded';
    } catch (err) {
      console.warn('[W4S Cart to Quote] Klaviyo call failed', err);
      return 'not_loaded';
    }
  };

  /* ------------------------------------------------------------ open / close */

  // The dialog has two views: the details form and the post-print success message.
  const showFormView = () => {
    form.hidden = false;
    if (doneEl) doneEl.hidden = true;
    dialog.setAttribute('aria-labelledby', 'dialog-title');
  };

  const showModal = () => {
    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }
  };

  const showDoneView = (quote) => {
    if (!doneEl) return;
    const text = doneEl.querySelector('[data-done-text]');
    if (text) text.textContent = (text.dataset.template || '').replace('[number]', quote.number);
    form.hidden = true;
    doneEl.hidden = false;
    dialog.setAttribute('aria-labelledby', 'done-title');
    showModal();
    doneEl.querySelector('#done-title')?.focus();
  };

  // Close the dialog and show the cart: open the cart drawer if the theme has one,
  // stay put on the cart page, otherwise go to the cart page.
  const viewCart = () => {
    closeDialog();
    const onCartPage = window.location.pathname.replace(/\/$/, '') === cartRoot();
    if (onCartPage) return;
    if (document.querySelector('cart-drawer, #cart-drawer')) {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener } }));
    } else {
      window.location.href = cartRoot();
    }
  };

  const openDialog = () => {
    showFormView();
    showError('');
    restoreGuestDetails();
    showModal();
    const firstEmpty = Array.from(form.querySelectorAll('input[required]')).find((el) => !el.value);
    (firstEmpty || form.querySelector('input, textarea'))?.focus();
  };

  const closeDialog = () => {
    if (dialog.open) dialog.close();
  };

  dialog.addEventListener('close', () => {
    if (opener && document.contains(opener)) opener.focus();
  });

  // Click on the backdrop closes the dialog.
  dialog.addEventListener('click', (evt) => {
    if (evt.target === dialog) closeDialog();
  });

  root.querySelector('[data-close]')?.addEventListener('click', closeDialog);
  root.querySelectorAll('[data-done-close]').forEach((btn) => btn.addEventListener('click', closeDialog));
  root.querySelector('[data-view-cart]')?.addEventListener('click', viewCart);
  root.querySelector('[data-reprint]')?.addEventListener('click', () => {
    if (!lastQuote) return;
    closeDialog();
    printQuote(() => showDoneView(lastQuote));
  });

  // Delegated so buttons survive cart page / drawer section re-renders.
  document.addEventListener('click', (evt) => {
    const trigger = evt.target.closest('[data-w4s-quote-open]');
    if (!trigger) return;
    evt.preventDefault();

    const drawer = trigger.closest('#cart-drawer');
    if (drawer && drawer.hasAttribute('open') && typeof drawer.close === 'function') {
      // Close the cart drawer first so its focus trap doesn't fight the dialog.
      opener = document.querySelector('[data-w4s-quote-open][data-w4s-quote-context="page"]') || null;
      drawer.close(openDialog);
    } else {
      opener = trigger;
      openDialog();
    }
  });

  /* ------------------------------------------------------------- the quote */

  // Link that rebuilds this cart. Prefers the share link (keeps properties and selling plans).
  // Both kinds carry the quote number, which lands on the cart (and order) as quote_ref.
  const orderLinkFor = async (cart, quoteNumber) => {
    if (!config.showOrderLink || !cart.items.length) return null;
    if (window.W4SCartShare && typeof window.W4SCartShare.linkFor === 'function') {
      try {
        const link = await window.W4SCartShare.linkFor(cart, { quoteRef: quoteNumber });
        if (link) return link;
      } catch (err) {
        console.warn('[W4S Cart to Quote] Share link failed; using a cart permalink', err);
      }
    }
    // Permalinks drop all line-item properties (hidden ones too) and can't carry selling plans.
    const hasCustomisedLines = cart.items.some((item) => (
      (item.properties && Object.keys(item.properties).length > 0) || item.selling_plan_allocation
    ));
    if (hasCustomisedLines) return null;
    const permalink = `${config.shopUrl}/cart/${cart.items.map((item) => `${item.variant_id}:${item.quantity}`).join(',')}`;
    return quoteNumber ? `${permalink}?attributes[quote_ref]=${encodeURIComponent(quoteNumber)}` : permalink;
  };

  const buildQuote = (cart, details, orderLink, number) => {
    const now = new Date();

    return {
      number: number || makeQuoteNumber(now),
      createdAt: now.toISOString(),
      currency: cart.currency,
      customer: details,
      items: cart.items.map((item) => ({
        variantId: item.variant_id,
        productId: item.product_id,
        title: item.product_title,
        variant: item.product_has_only_default_variant ? '' : (item.variant_title || ''),
        sku: item.sku || '',
        image: item.image || (item.featured_image && item.featured_image.url) || '',
        url: item.url ? `${config.shopUrl}${item.url}` : '',
        properties: visibleProperties(item.properties),
        sellingPlan: item.selling_plan_allocation ? item.selling_plan_allocation.selling_plan.name : '',
        quantity: item.quantity,
        unitPrice: item.final_price,
        originalUnitPrice: item.original_price,
        lineTotal: item.final_line_price,
        originalLineTotal: item.original_line_price,
        discounts: (item.line_level_discount_allocations || []).map((d) => ({
          title: d.discount_application ? d.discount_application.title : '',
          amount: d.amount
        }))
      })),
      subtotal: cart.items_subtotal_price,
      cartDiscounts: (cart.cart_level_discount_applications || []).map((d) => ({
        title: d.title,
        amount: d.total_allocated_amount
      })),
      total: cart.total_price,
      note: cart.note || '',
      orderLink
    };
  };

  const renderQuote = (quote) => {
    const money = makeMoneyFormatter(quote.currency);
    const c = quote.customer;
    const fullName = [c.first_name, c.last_name].filter(Boolean).join(' ');

    const itemRows = quote.items.map((item) => {
      const meta = [];
      if (item.variant) meta.push(escapeHtml(item.variant));
      if (item.sku) meta.push(`SKU: ${escapeHtml(item.sku)}`);
      if (item.sellingPlan) meta.push(escapeHtml(item.sellingPlan));
      const props = item.properties.map(([key, value]) => (
        `<li><span>${escapeHtml(key)}:</span> ${escapeHtml(value)}</li>`
      )).join('');
      const discounts = item.discounts.filter((d) => d.amount > 0).map((d) => (
        `<li class="q__discount">${escapeHtml(d.title || 'Discount')} (−${money(d.amount)})</li>`
      )).join('');
      const wasPrice = item.originalUnitPrice > item.unitPrice
        ? `<s class="q__was">${money(item.originalUnitPrice)}</s>`
        : '';

      return `
        <tr>
          <td class="q__item"><div class="q__item-inner">
            ${item.image ? `<img src="${escapeHtml(sizedImage(item.image, 160))}" alt="" width="56" height="56">` : '<span class="q__noimg"></span>'}
            <div>
              <p class="q__title">${escapeHtml(item.title)}</p>
              ${meta.length ? `<p class="q__meta">${meta.join(' · ')}</p>` : ''}
              ${props || discounts ? `<ul class="q__props">${props}${discounts}</ul>` : ''}
            </div>
          </div></td>
          <td class="q__num">${item.quantity}</td>
          <td class="q__num">${wasPrice}${money(item.unitPrice)}</td>
          <td class="q__num">${money(item.lineTotal)}</td>
        </tr>`;
    }).join('');

    const cartDiscountRows = quote.cartDiscounts.filter((d) => d.amount > 0).map((d) => `
      <tr><th scope="row">${escapeHtml(d.title || 'Discount')}</th><td>−${money(d.amount)}</td></tr>`).join('');

    const recipientLines = [
      fullName && `<strong>${escapeHtml(fullName)}</strong>`,
      c.company && escapeHtml(c.company),
      c.email && escapeHtml(c.email),
      c.phone && escapeHtml(c.phone)
    ].filter(Boolean).join('<br>');

    printRoot.innerHTML = `
      <article class="q">
        <header class="q__header">
          <div class="q__brand">
            ${config.logo
              ? `<img class="q__logo" src="${escapeHtml(config.logo)}" alt="${escapeHtml(config.shopName)}" style="width:${Number(config.logoWidth) || 180}px">`
              : `<p class="q__shopname">${escapeHtml(config.shopName)}</p>`}
            ${config.companyDetails ? `<p class="q__company">${nl2br(config.companyDetails)}</p>` : ''}
          </div>
          <div class="q__docinfo">
            <h1>Quote</h1>
            <dl>
              <dt>Quote no.</dt><dd>${escapeHtml(quote.number)}</dd>
              <dt>Date</dt><dd>${formatDate(new Date(quote.createdAt))}</dd>
            </dl>
          </div>
        </header>

        <section class="q__parties">
          <div>
            <h2>Prepared for</h2>
            <p>${recipientLines}</p>
          </div>
          ${c.reference ? `<div><h2>Reference / notes</h2><p>${nl2br(c.reference)}</p></div>` : ''}
        </section>

        <table class="q__lines">
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col" class="q__num">Qty</th>
              <th scope="col" class="q__num">Unit price</th>
              <th scope="col" class="q__num">Total</th>
            </tr>
          </thead>
          <tbody>${itemRows}</tbody>
        </table>

        <table class="q__totals">
          <tbody>
            <tr><th scope="row">Subtotal</th><td>${money(quote.subtotal)}</td></tr>
            ${cartDiscountRows}
            <tr class="q__grand"><th scope="row">Total (${escapeHtml(quote.currency)})</th><td>${money(quote.total)}</td></tr>
          </tbody>
        </table>
        <p class="q__taxnote">Prices are subject to change without notice. Taxes and shipping are calculated at checkout.</p>

        ${quote.note ? `<section class="q__block"><h2>Order note</h2><p>${nl2br(quote.note)}</p></section>` : ''}

        ${quote.orderLink
          ? `<section class="q__order">
              <h2>Ready to order?</h2>
              <p class="q__order-lead">Use the button to load everything on this quote into your cart, then check out as usual.</p>
              <a class="q__order-btn" href="${escapeHtml(quote.orderLink)}">Load quote ${escapeHtml(quote.number)} into my cart</a>
              <div class="q__order-note">
                <p>The button adds the items, quantities and any custom options shown above. It doesn\u2019t carry over:</p>
                <ul>
                  <li><strong>Discounts:</strong> discount codes need to be entered again at checkout, and automatic discounts apply only if they\u2019re still running.</li>
                  <li><strong>The prices on this quote:</strong> your cart uses today\u2019s prices, which may differ. Any trade or account pricing applies only when you\u2019re signed in.</li>
                  <li><strong>Unavailable items:</strong> anything that has since sold out or been discontinued is left out, and you\u2019ll be told which.</li>
                </ul>
                <p>Shipping and taxes are calculated at checkout. Viewing a printed copy? Open the PDF on your computer or phone to use the button.</p>
              </div>
            </section>`
          : `<section class="q__block">
              <h2>How to order</h2>
              <p>Order online at <a href="${escapeHtml(config.shopUrl)}">${escapeHtml(config.shopUrl.replace(/^https?:\/\//, ''))}</a>, or contact us and include a copy of this quote.</p>
            </section>`}

        ${config.termsText ? `<footer class="q__terms">${nl2br(config.termsText)}</footer>` : ''}
      </article>`;
  };

  const waitForImages = (root, timeout = 4000) => {
    const imgs = Array.from(root.querySelectorAll('img')).filter((img) => !img.complete);
    if (!imgs.length) return Promise.resolve();
    const loads = imgs.map((img) => new Promise((resolve) => {
      img.addEventListener('load', resolve, { once: true });
      img.addEventListener('error', resolve, { once: true });
    }));
    return Promise.race([Promise.all(loads), new Promise((resolve) => setTimeout(resolve, timeout))]);
  };

  // Page-level print isolation exists only while printing, then is removed again.
  const printQuote = (onDone) => {
    const style = document.createElement('style');
    style.id = PRINT_STYLE_ID;
    style.textContent = `@media print {
      @page { margin: 14mm; }
      html, body { background: #fff !important; height: auto !important; min-height: 0 !important; overflow: visible !important; margin: 0 !important; padding: 0 !important; }
      body > :not(w4s-cart-quote) { display: none !important; }
    }`;

    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      host.classList.remove('is-printing');
      document.getElementById(PRINT_STYLE_ID)?.remove();
      if (typeof onDone === 'function') onDone();
    };

    document.getElementById(PRINT_STYLE_ID)?.remove();
    document.head.appendChild(style);
    host.classList.add('is-printing');
    window.addEventListener('afterprint', cleanup, { once: true });

    // Let the dialog close and layout settle before opening the print window.
    requestAnimationFrame(() => setTimeout(() => {
      window.print();
      // Some mobile browsers don't fire afterprint; fall back after a delay.
      setTimeout(cleanup, 1000);
    }, 50));
  };

  form.addEventListener('submit', async (evt) => {
    evt.preventDefault();
    showError('');

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    const details = {};
    ['first_name', 'last_name', 'email', 'company', 'phone', 'reference'].forEach((name) => {
      details[name] = (form.elements[name]?.value || '').trim();
    });

    const marketingConsent = !!form.elements.accepts_marketing?.checked;

    setLoading(true);
    try {
      const response = await fetch(`${cartRoot()}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
      const cart = await response.json();

      if (!cart.items || !cart.items.length) {
        showError('Your cart is empty. Add some items before creating a quote.');
        return;
      }

      // Runs alongside building the quote; never stops the quote from opening.
      const marketingPromise = handleMarketingConsent(details, marketingConsent);

      const quoteNumber = makeQuoteNumber(new Date());
      const quote = buildQuote(cart, details, await orderLinkFor(cart, quoteNumber), quoteNumber);
      renderQuote(quote);
      rememberGuestDetails(details);
      const storeCopyPromise = marketingPromise.then((m) => sendStoreCopy(quote, m));
      await waitForImages(printRoot);
      const marketing = await marketingPromise;
      const klaviyo = sendToKlaviyo(quote, marketing);
      const ga4 = trackLead(quote);
      const storeCopy = await storeCopyPromise;

      document.dispatchEvent(new CustomEvent('w4s:quote:created', {
        detail: { quote, marketing, klaviyo, storeCopy, ga4 }
      }));

      closeDialog();

      lastQuote = quote;
      // When the print window closes, come back to the dialog with a thank-you message.
      printQuote(() => showDoneView(quote));
    } catch (err) {
      console.error('[W4S Cart to Quote]', err);
      showError('Sorry, we couldn’t create your quote. Please try again.');
    } finally {
      setLoading(false);
    }
  });

  window.W4SCartQuote = { open: openDialog, close: closeDialog };
})();
